/*
 * estados.js — Lógica del sistema de estados del proceso mensual.
 *
 * Ver diseno/estados.md y las tarjetas C-003 y C-004 de diseno/CAMBIOS.md.
 *
 * Lo guardado en la base de datos no cambia (rojo, amarillo, verde, azul, rosa).
 * Aquí se calcula lo que se muestra: bloqueado, por hacer, en proceso, completado,
 * revisado o presentado en $0, más el modificador "atrasado" con sus días.
 *
 * Todo es lógica pura (sin React) para poder reutilizarla en los filtros (C-012).
 */

// ───────────── Fechas límite ─────────────
//
// "A más tardar día N" del mes siguiente al periodo (salvo `mesesDespues`): la contabilidad
// de agosto se trabaja en septiembre, así que Solicitud de agosto vence el 1 de septiembre.
//
// Un paso sin fecha configurada nunca se marca atrasado. Para cambiar o agregar una fecha
// basta con tocar esta lista; ningún otro lugar (ni el servidor) tiene copia.
//
// Los procesos se reconocen por palabras de su nombre (sin acentos ni mayúsculas),
// igual que el backend. Después, todo se consulta por ID de proceso.
//
// `mesesDespues`: cuántos meses después del periodo cae la fecha (1 si no se dice). Pago usa 2:
// casi nunca se sabe cuándo paga el cliente y uno se entera con la contabilidad del mes siguiente,
// así que el pago de septiembre se espera para el 17 de noviembre (C-032, Agustín, 22 oct).
// Fechas de la sesión con el equipo del 27 oct (C-032): Contabilidad el 12, Cálculo el 13 (incluye
// la revisión de Pedro) y Declaración y Envío el 14, para que el cliente tenga tres días para
// pagar antes del 17. La Solicitud sale sola el día 1.
//
// `habil: true` (solo la Solicitud): si el día cae en sábado o domingo, el límite pasa al lunes,
// porque el correo sale ese lunes y la Solicitud se marca sola al salir (Jimena, 3 nov). Los demás
// pasos conservan su día aunque caiga en fin de semana. Los feriados no se consideran.
//
// Solo aplican a los pasos de la cascada general (categoría 1): "Cálculo de cuotas IMSS",
// "Pago IMSS" o "Anual · Cálculo" también contienen esas palabras y no deben tomar estas fechas.
export const FECHAS_LIMITE = [
  { palabras: ['solicitud', 'informacion'], dia: 1, habil: true },
  { palabras: ['recepcion', 'documentos'], dia: 6 },
  { palabras: ['contabilidad'], dia: 12 },
  { palabras: ['calculo', 'impuestos'], dia: 13 },
  { palabras: ['declaracion', 'presentada'], dia: 14 },
  { palabras: ['envio', 'declaracion'], dia: 14 },
  { palabras: ['pago', 'impuestos'], dia: 17, mesesDespues: 2 },
];

export const normalizarNombre = (nombre) =>
  (nombre || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const tienePalabras = (proceso, palabras) => {
  const nombre = normalizarNombre(proceso?.nombre);
  return palabras.every(p => nombre.includes(p));
};

// Nombre que se muestra en pantalla (C-019). Los encabezados salen de aquí y no de la base de
// datos, para que sean iguales en producción ("Envio Declaracion") y en la demo. No se cambia
// la base porque el backend busca los procesos por nombre. Las palabras son estrictas para no
// confundir "Cálculo ISR" o "Pago IMSS" (obligaciones adicionales) con pasos de la cascada.
const NOMBRES_VISIBLES = [
  { palabras: ['solicitud'], nombre: 'Solicitud de información', cascada: true },
  { palabras: ['recepcion', 'documentos'], nombre: 'Recepción de documentos', cascada: true },
  { palabras: ['contabilidad', 'iniciada'], nombre: 'Contabilidad', cascada: true },
  { palabras: ['calculo', 'impuestos'], nombre: 'Cálculo de impuestos', cascada: true },
  { palabras: ['declaracion', 'presentada'], nombre: 'Declaración presentada', cascada: true },
  { palabras: ['envio', 'declaracion'], nombre: 'Envío de declaración al cliente', cascada: true },
  { palabras: ['pago', 'impuestos'], nombre: 'Pago de impuestos', cascada: true },
  { palabras: ['opinion', 'cumplimiento'], nombre: 'Opinión de cumplimiento', cascada: true },
  { palabras: ['1-80', 'mensual'], nombre: 'Impuesto sobre nómina (1.8%) mensual' },
  { palabras: ['1-80', 'trimestral'], nombre: 'Impuesto sobre nómina (1.8%) trimestral' },
];

/** Recibe el nombre guardado (o el proceso) y devuelve el que se muestra. */
export const nombreVisibleProceso = (procesoONombre) => {
  const esTexto = typeof procesoONombre === 'string';
  const nombre = esTexto ? procesoONombre : procesoONombre?.nombre;
  // Con el proceso completo se sabe si es de la tabla mensual (categoría 1); con solo el texto,
  // bastan las palabras.
  const categoria = esTexto ? null : procesoONombre?.categoria_id;
  const general = categoria == null || Number(categoria) === 1;
  const regla = NOMBRES_VISIBLES.find(r => (!r.cascada || general) && tienePalabras({ nombre }, r.palabras));
  return regla ? regla.nombre : (nombre || '');
};

export const esDeclaracionPresentada = (proceso) =>
  tienePalabras(proceso, ['declaracion', 'presentada']);

// Opinión de cumplimiento es un resultado, no un paso de trabajo: queda fuera de la cascada.
export const esOpinionCumplimiento = (proceso) => tienePalabras(proceso, ['opinion']);

const esDeLaCascadaGeneral = (proceso) => proceso?.categoria_id == null || Number(proceso.categoria_id) === 1;

// { [proceso_id]: { dia, mesesDespues, habil? } }
export const construirFechasLimite = (procesos) => {
  const mapa = {};
  for (const proceso of procesos || []) {
    if (!esDeLaCascadaGeneral(proceso)) continue;
    const regla = FECHAS_LIMITE.find(r => tienePalabras(proceso, r.palabras));
    if (regla) mapa[proceso.id] = { dia: regla.dia, mesesDespues: regla.mesesDespues || 1, ...(regla.habil && { habil: true }) };
  }
  return mapa;
};

// ───────────── Atraso ─────────────

const MS_DIA = 24 * 60 * 60 * 1000;

const inicioDelDia = (fecha) => new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());

/**
 * Una fecha límite es { dia, mesesDespues }. También se acepta solo el día (un número), que
 * equivale a { dia, mesesDespues: 1 }: así la usan Obligaciones adicionales.
 */
const comoLimite = (limite) => {
  if (!limite) return null;
  const l = typeof limite === 'number' ? { dia: limite } : limite;
  return l.dia ? { dia: l.dia, mesesDespues: l.mesesDespues || 1, habil: !!l.habil } : null;
};

/**
 * Fecha límite real de un paso: día N, `mesesDespues` meses después del periodo.
 * `periodo` = { mes: 1-12, anio }. new Date corrige el año solo (mes 13 = enero siguiente).
 */
export const fechaLimiteDe = (limite, periodo) => {
  const l = comoLimite(limite);
  if (!l || !periodo) return null;
  const fecha = new Date(periodo.anio, periodo.mes - 1 + l.mesesDespues, l.dia);
  // Sábado o domingo → lunes (solo las fechas `habil`)
  while (l.habil && (fecha.getDay() === 0 || fecha.getDay() === 6)) fecha.setDate(fecha.getDate() + 1);
  return fecha;
};

/** Días que faltan para la fecha límite: 0 si vence hoy, null si ya venció o no tiene fecha (C-024). */
export const calcularDiasParaVencer = (limiteDelPaso, periodo, hoy = new Date()) => {
  const limite = fechaLimiteDe(limiteDelPaso, periodo);
  if (!limite) return null;
  const dias = Math.round((limite - inicioDelDia(hoy)) / MS_DIA);
  return dias >= 0 ? dias : null;
};

/** "Por vencer": el paso actual vence hoy o en los próximos 3 días (C-024). */
export const DIAS_POR_VENCER = 3;

/**
 * Días de atraso de un paso.
 * Devuelve 0 si no tiene fecha límite o si todavía no vence.
 */
export const calcularDiasAtraso = (limiteDelPaso, periodo, hoy = new Date()) => {
  const limite = fechaLimiteDe(limiteDelPaso, periodo);
  if (!limite) return 0;
  const dias = Math.round((inicioDelDia(hoy) - limite) / MS_DIA);
  return dias > 0 ? dias : 0;
};

// ───────────── Fechas cortas ─────────────

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/**
 * "6 oct" (o "6 oct 2025" si no es del año en curso). Evita "6/10/2026", que se puede leer
 * como 6 de octubre o como 10 de junio.
 */
export const fechaCorta = (fecha, hoy = new Date()) => {
  if (!fecha) return '';
  const d = fecha instanceof Date ? fecha : new Date(fecha);
  if (Number.isNaN(d.getTime())) return '';
  const base = `${d.getDate()} ${MESES_CORTOS[d.getMonth()]}`;
  return d.getFullYear() === hoy.getFullYear() ? base : `${base} ${d.getFullYear()}`;
};

// ───────────── Revisión de Pedro (C-014) ─────────────
//
// Campo `revision_pedro` del cliente: no (por defecto) · calculo ("Contabilidad y cálculo", C-033).
// Pedro revisa contabilidad y cálculo juntos y lo marca en el paso Cálculo. En ese paso, Completado
// (verde) se ve "Por revisar" y queda terminado solo cuando está Revisado (azul).
// La misma regla vive en backend/revision.js.
export const OPCIONES_REVISION = [
  { valor: 'no', texto: 'No necesita' },
  { valor: 'calculo', texto: 'Contabilidad y cálculo' },
];

/** `calculo_declaracion` (C-014) ya no se ofrece; si un cliente lo tiene guardado, cuenta como `calculo`. */
export const normalizarRevision = (revision) => (revision === 'calculo_declaracion' ? 'calculo' : revision || 'no');

const PASOS_REVISABLES = {
  calculo: [['calculo']],
};

const ETIQUETAS_REVISION = {
  calculo: 'Pedro revisa: contabilidad y cálculo',
};

/** "Pedro revisa: contabilidad y cálculo" o null si el cliente no necesita revisión. */
export const etiquetaRevision = (cliente) => ETIQUETAS_REVISION[normalizarRevision(cliente?.revision_pedro)] || null;

/** Si Pedro revisa este paso de la cascada en este cliente. */
export const esPasoRevisable = (cliente, proceso) =>
  !esOpinionCumplimiento(proceso) &&
  (PASOS_REVISABLES[normalizarRevision(cliente?.revision_pedro)] || []).some(palabras => tienePalabras(proceso, palabras));

// ───────────── Cascada y paso actual ─────────────

const estadoGuardado = (cliente, procesoId) =>
  cliente?.estados?.find(e => e.proceso_id === procesoId)?.estado || 'rojo';

// ───────────── Presentada en $0 (C-034) ─────────────
//
// Al marcar "Presentada en $0" se guarda el motivo (`motivo_cero` en la fila del estado).
// Solo "Sin movimientos" es un cero de verdad. Los otros dos son provisionales para evitar la
// multa: falta presentar la complementaria, así que el paso no cuenta como terminado.
// Sin motivo (marcado antes de C-034) cuenta como terminado, como hasta ahora.
// La misma lista vive en backend/server.js (MOTIVOS_CERO).
export const MOTIVOS_CERO = [
  { valor: 'sin_movimientos', texto: 'Sin movimientos', detalle: 'El cliente no tuvo ingresos ni gastos. Queda como terminado.' },
  { valor: 'no_pago', texto: 'Con movimientos, el cliente no pagó', detalle: 'Cuando quiera pagar se presenta la complementaria.', pendiente: 'el cliente no pagó' },
  { valor: 'falta_info', texto: 'Con movimientos, falta información', detalle: 'Cuando llegue la información se presenta la correcta.', pendiente: 'falta información' },
];

const motivoGuardado = (cliente, procesoId) =>
  cliente?.estados?.find(e => e.proceso_id === procesoId)?.motivo_cero || null;

/** Motivo provisional ("el cliente no pagó" / "falta información") o null. */
export const motivoProvisional = (motivo) => MOTIVOS_CERO.find(m => m.valor === motivo && m.pendiente) || null;

/** Si la declaración del cliente está presentada en $0 de forma provisional (falta la complementaria). */
export const complementariaPendiente = (cliente, proceso) =>
  esDeclaracionPresentada(proceso) && estadoGuardado(cliente, proceso.id) === 'rosa'
  && !!motivoProvisional(motivoGuardado(cliente, proceso.id));

/**
 * `revisable`: si Pedro revisa ese paso en ese cliente; entonces Completado todavía no basta.
 * `motivo`: motivo de "Presentada en $0"; si es provisional, la declaración todavía no termina (C-034).
 */
export const estaTerminado = (estado, proceso, revisable = false, motivo = null) =>
  estado === 'azul' || (estado === 'verde' && !revisable)
  || (estado === 'rosa' && esDeclaracionPresentada(proceso) && !motivoProvisional(motivo));

const terminadoEnCliente = (cliente, proceso, revisable) =>
  estaTerminado(estadoGuardado(cliente, proceso.id), proceso, revisable, motivoGuardado(cliente, proceso.id));

/**
 * "4 de 7" (C-024): pasos de la cascada que el contador ya terminó (Completado, Revisado o
 * Presentado en $0), sin contar la Opinión de cumplimiento. Una presentada en $0 provisional
 * no suma (C-034).
 */
export const avancePasos = (cliente, procesos) => {
  const cascada = (procesos || []).filter(p => !esOpinionCumplimiento(p));
  const terminados = cascada.filter(p =>
    ['verde', 'azul'].includes(estadoGuardado(cliente, p.id))
    || (estadoGuardado(cliente, p.id) === 'rosa' && !complementariaPendiente(cliente, p))).length;
  return { terminados, total: cascada.length };
};

/** Pasos de la cascada que esperan la revisión de Pedro (Completados sin Revisado). */
export const pasosPorRevisar = (cliente, procesos) =>
  (procesos || []).filter(p => esPasoRevisable(cliente, p) && estadoGuardado(cliente, p.id) === 'verde');

/**
 * Primer paso revisable antes de `proceso` que todavía no está Revisado, o null.
 * Mientras exista, `proceso` no se puede marcar (solo regresar a Por hacer).
 */
export const revisionPendienteAntes = (cliente, proceso, procesos) => {
  const cascada = (procesos || []).filter(p => !esOpinionCumplimiento(p));
  const indice = cascada.findIndex(p => p.id === proceso?.id);
  if (indice <= 0) return null;
  return cascada.slice(0, indice).find(p =>
    esPasoRevisable(cliente, p) && !terminadoEnCliente(cliente, p, true)) || null;
};

/**
 * Paso actual de un cliente: la primera columna de la cascada (sin Opinión de
 * cumplimiento) que no está terminada.
 *
 * `procesos` son los procesos generales en su orden.
 * Devuelve null si el cliente ya terminó toda la cascada.
 */
export const calcularPasoActual = (cliente, procesos, fechasLimite, periodo, hoy = new Date()) => {
  const cascada = (procesos || []).filter(p => !esOpinionCumplimiento(p));
  const indice = cascada.findIndex(p => !terminadoEnCliente(cliente, p, esPasoRevisable(cliente, p)));
  if (indice === -1) return null;
  const proceso = cascada[indice];
  const limitePaso = fechasLimite?.[proceso.id] || null;
  // Completado pero sin Revisado: el contador ya terminó, así que no cuenta como atraso (C-014).
  const porRevisar = estadoGuardado(cliente, proceso.id) === 'verde';
  // Presentada en $0 provisional: se presentó a tiempo; falta la complementaria, sin atraso (C-034).
  const complementaria = complementariaPendiente(cliente, proceso);
  const sinAtraso = porRevisar || complementaria;
  return {
    proceso,
    indice,
    limitePaso,
    diasAtraso: sinAtraso ? 0 : calcularDiasAtraso(limitePaso, periodo, hoy),
    // C-024: días que faltan para la fecha límite (0 = vence hoy); null si ya venció o no tiene fecha.
    diasParaVencer: sinAtraso ? null : calcularDiasParaVencer(limitePaso, periodo, hoy),
    porRevisar,
    complementaria,
    siguiente: cascada[indice + 1] || null,
  };
};

// ───────────── Pasos de la cascada (embudo de C-012) ─────────────

// Nombre corto e identificador para la URL (?paso=documentos). Se reconocen por
// palabras del nombre, igual que las fechas límite. El orden es el de `procesos`.
const PASOS_CORTOS = [
  { slug: 'solicitud', corto: 'Solicitud', palabras: ['solicitud'] },
  { slug: 'documentos', corto: 'Documentos', palabras: ['recepcion', 'documentos'] },
  { slug: 'contabilidad', corto: 'Contabilidad', palabras: ['contabilidad'] },
  { slug: 'calculo', corto: 'Cálculo', palabras: ['calculo'] },
  { slug: 'declaracion', corto: 'Declaración', palabras: ['declaracion', 'presentada'] },
  { slug: 'envio', corto: 'Envío', palabras: ['envio'] },
  { slug: 'pago', corto: 'Pago', palabras: ['pago'] },
];

export const PASO_TERMINADOS = 'terminados';

// C-024: qué es cada paso y quién lo marca (tooltip del encabezado y "¿Cómo funciona el mes?").
export const INFO_PASOS = {
  solicitud: { que: 'Correo al cliente pidiendo sus documentos.', quien: 'Lo marca el contador.' },
  documentos: { que: 'El cliente mandó lo que se le pidió.', quien: 'Lo marca el contador.' },
  contabilidad: { que: 'Se hace en Contpaq.', quien: 'Lo marca el contador.' },
  calculo: { que: 'En Excel, con los datos de la contabilidad.', quien: 'Lo marca el contador. Pedro lo revisa en los clientes que lo necesitan.' },
  declaracion: { que: 'En el portal del SAT. Puede ser "Presentada en $0", aunque falten pasos; si fue provisional, queda "Complementaria pendiente".', quien: 'Lo marca el contador.' },
  envio: { que: 'Se le manda la declaración al cliente.', quien: 'Lo marca el contador.' },
  pago: { que: 'Lo paga el cliente; se confirma en el SAT o cuando avisa.', quien: 'Lo marca el contador.' },
  opinion: { que: 'PDF del SAT: positiva o negativa.', quien: 'Lo marca el contador. Solo informa: no cuenta para el avance.' },
};

// Etapas de la banda de arriba de los encabezados (por número de paso).
export const ETAPAS = [
  { nombre: 'Recopilación', pasos: 2 },
  { nombre: 'Contabilidad y cálculo', pasos: 2 },
  { nombre: 'Declaración y pago', pasos: 3 },
];

/** Pasos de la cascada (sin Opinión) con número, nombre corto y slug. */
export const pasosCascada = (procesos) =>
  (procesos || [])
    .filter(p => !esOpinionCumplimiento(p))
    .map((proceso, i) => {
      const corto = PASOS_CORTOS.find(c => tienePalabras(proceso, c.palabras));
      return {
        proceso,
        numero: i + 1,
        slug: corto ? corto.slug : `paso-${proceso.id}`,
        corto: corto ? corto.corto : proceso.nombre,
      };
    });

// ───────────── Estado visible ─────────────

export const NOMBRES_ESTADO = {
  'bloqueado': 'Bloqueado',
  'por-hacer': 'Por hacer',
  'en-proceso': 'En proceso',
  'completado': 'Completado',
  'revisado': 'Revisado',
  'presentado-cero': 'Presentado en $0',
};

// Sello de la revisión de Pedro, encima del círculo de Completado (C-014).
export const NOMBRES_SELLO = {
  'por-revisar': 'Por revisar',
  'revisado': 'Revisado',
  'complementaria': 'Complementaria pendiente', // C-034: cuadrado ámbar, no círculo
};

// ───────────── Opinión de cumplimiento (C-006) ─────────────
//
// Es un resultado, no un paso: Positiva, Negativa o todavía sin sacar.
// Se guarda en la misma tabla de estados:
//   verde (o azul) → Positiva · negativa → Negativa · rojo / sin registro → Sin sacar
// "negativa" es un valor nuevo que solo se usa en este proceso.
export const OPINION_NEGATIVA = 'negativa';

export const NOMBRES_OPINION = {
  'opinion-positiva': 'Positiva',
  'opinion-negativa': 'Negativa',
};

// Opciones del menú de la columna: valor guardado y texto.
export const OPCIONES_OPINION = [
  { estado: 'verde', texto: 'Positiva' },
  { estado: OPINION_NEGATIVA, texto: 'Negativa' },
  { estado: 'rojo', texto: 'Sin sacar' },
];

/** 'positiva' | 'negativa' | null (sin sacar) a partir del valor guardado. */
export const resultadoOpinion = (estado) => {
  if (estado === 'verde' || estado === 'azul') return 'positiva';
  if (estado === OPINION_NEGATIVA) return 'negativa';
  return null;
};

// Valor guardado → estado visible "por sí solo" (sin cascada). Para historial y textos.
const VISIBLE_POR_GUARDADO = {
  rojo: 'por-hacer',
  amarillo: 'en-proceso',
  verde: 'completado',
  azul: 'revisado',
  rosa: 'presentado-cero',
  [OPINION_NEGATIVA]: 'opinion-negativa',
};

export const claveVisibleDeGuardado = (estado) => VISIBLE_POR_GUARDADO[estado] || null;

export const nombreEstadoGuardado = (estado) => {
  const clave = claveVisibleDeGuardado(estado);
  return NOMBRES_ESTADO[clave] || NOMBRES_OPINION[clave] || 'Sin estado';
};

const textoAtraso = (dias, limitePaso, periodo, hoy) => {
  const limite = fechaLimiteDe(limitePaso, periodo);
  const cuando = limite ? fechaCorta(limite, hoy) : `día ${comoLimite(limitePaso)?.dia}`;
  return `Venció el ${cuando}, atrasado ${dias} ${dias === 1 ? 'día' : 'días'}`;
};

// C-024: "Vence el 14 oct, falta 1 día" (hoy, falta 1 día, faltan N días)
const textoVence = (diasRestantes, limitePaso, periodo, hoy) => {
  const limite = fechaLimiteDe(limitePaso, periodo);
  const cuando = limite ? fechaCorta(limite, hoy) : `día ${comoLimite(limitePaso)?.dia}`;
  const falta = diasRestantes === 0 ? 'hoy' : diasRestantes === 1 ? 'falta 1 día' : `faltan ${diasRestantes} días`;
  return `Vence el ${cuando}, ${falta}`;
};

/**
 * Estado visible de un proceso para un cliente.
 *
 * contexto:
 *   - cascada: true para los procesos generales de la tabla mensual; false para
 *     Actividades Especiales y otras obligaciones independientes.
 *   - pasoActual: resultado de calcularPasoActual (solo si cascada).
 *   - fechasLimite, periodo, hoy: para calcular el atraso de pasos independientes.
 *   - estado: valor guardado; si no se pasa, se lee de cliente.estados.
 *
 * Devuelve { clave, nombre, atrasado, dias, limitePaso, tooltip }.
 */
export const estadoVisible = (cliente, proceso, contexto = {}) => {
  const {
    cascada = true, pasoActual = null, fechasLimite = {}, periodo = null, hoy = new Date(),
  } = contexto;
  const estado = contexto.estado || estadoGuardado(cliente, proceso.id);

  let clave;
  let atrasado = false;
  let dias = 0;
  let limitePaso = null;
  let tooltip;
  let sello = null;
  let esperaRevision = false;
  let diasRestantes = null; // C-024: por vencer (0-3 días)

  const fueraDeCascada = !cascada || esOpinionCumplimiento(proceso);
  const opinion = esOpinionCumplimiento(proceso) ? resultadoOpinion(estado) : null;

  if (opinion) clave = `opinion-${opinion}`;
  else if (estado === 'verde') {
    clave = 'completado';
    if (!fueraDeCascada && esPasoRevisable(cliente, proceso)) {
      sello = 'por-revisar';
      tooltip = 'Por revisar: falta que Pedro lo revise';
    }
  } else if (estado === 'azul') {
    clave = 'revisado';
    sello = 'revisado';
    tooltip = 'Revisado por Pedro';
  }
  else if (estado === 'rosa') {
    clave = 'presentado-cero';
    // C-034: el motivo dice si es un cero de verdad o falta la complementaria
    const motivo = motivoGuardado(cliente, proceso.id);
    const provisional = motivoProvisional(motivo);
    if (provisional && esDeclaracionPresentada(proceso)) {
      sello = 'complementaria';
      tooltip = `Presentada en $0 · Complementaria pendiente: ${provisional.pendiente}`;
    } else if (motivo === 'sin_movimientos') {
      tooltip = 'Presentada en $0 · Sin movimientos';
    }
  } else {
    clave = estado === 'amarillo' ? 'en-proceso' : 'por-hacer';

    if (fueraDeCascada) {
      // Obligación independiente: su propio atraso, nunca bloqueada.
      limitePaso = fechasLimite[proceso.id] || null;
      dias = calcularDiasAtraso(limitePaso, periodo, hoy);
    } else if (pasoActual && pasoActual.proceso.id === proceso.id) {
      limitePaso = pasoActual.limitePaso;
      dias = pasoActual.diasAtraso;
      const faltan = pasoActual.diasParaVencer;
      if (!dias && faltan !== null && faltan !== undefined && faltan <= DIAS_POR_VENCER) diasRestantes = faltan;
    } else if (clave === 'por-hacer' && esDeclaracionPresentada(proceso)) {
      // C-034: la declaración se puede presentar en ceros aunque falten pasos: nunca se bloquea.
      if (pasoActual?.porRevisar) {
        esperaRevision = pasoActual.siguiente?.id === proceso.id;
        tooltip = 'Por hacer · Espera la revisión de Pedro; se puede presentar en ceros';
      } else {
        tooltip = 'Por hacer · Se puede presentar en ceros aunque falten pasos';
      }
    } else if (clave === 'por-hacer') {
      // Rojo después del paso actual: todavía no puede empezar.
      clave = 'bloqueado';
      if (pasoActual?.complementaria) {
        tooltip = 'Bloqueado: falta la complementaria de la declaración';
      } else if (pasoActual?.porRevisar) {
        tooltip = 'Bloqueado: espera la revisión de Pedro';
        esperaRevision = pasoActual.siguiente?.id === proceso.id;
      } else {
        tooltip = pasoActual
          ? `Bloqueado: falta ${nombreVisibleProceso(pasoActual.proceso)}`
          : 'Bloqueado';
      }
    }
    atrasado = dias > 0;
  }

  const nombre = NOMBRES_ESTADO[clave] || NOMBRES_OPINION[clave];
  const porVencer = diasRestantes !== null;
  if (!tooltip) {
    tooltip = atrasado ? `${nombre} · ${textoAtraso(dias, limitePaso, periodo, hoy)}`
      : porVencer ? `${nombre} · ${textoVence(diasRestantes, limitePaso, periodo, hoy)}`
      : nombre;
  }

  return {
    clave, nombre, atrasado, dias, limitePaso, tooltip, sello, esperaRevision,
    textoAtraso: atrasado ? textoAtraso(dias, limitePaso, periodo, hoy) : null,
    porVencer,
    diasRestantes,
    textoVence: porVencer ? textoVence(diasRestantes, limitePaso, periodo, hoy) : null,
  };
};

/**
 * Texto del tooltip de un círculo de estado (C-022):
 * "Completado · actualizado el 7 oct por Yohali · venció el 6 oct, atrasado 3 días".
 * `fecha`: fecha_actualizacion; `por`: nombre de quien lo cambió (se usa el primer nombre).
 */
export const tooltipCirculo = (visible, { fecha = null, por = null, hoy = new Date() } = {}) => {
  const partes = [visible.atrasado || visible.porVencer ? visible.nombre : visible.tooltip];
  if (fecha) {
    const primerNombre = (por || '').trim().split(/\s+/)[0];
    partes.push(`actualizado el ${fechaCorta(fecha, hoy)}${primerNombre ? ` por ${primerNombre}` : ''}`);
  }
  const final = visible.atrasado ? visible.textoAtraso : visible.porVencer ? visible.textoVence : null;
  if (final) partes.push(final.charAt(0).toLowerCase() + final.slice(1));
  return partes.join(' · ');
};
